import { clinicalErrorIssueMessages } from '@/types/clinical-sources';
import { toast } from '@/hooks/use-toast';
import { AxiosError } from 'axios';
import { normalizeClientConsentAccessMessage } from '@/lib/clientConsentAccess';

/**
 * Error response structure from backend
 */
interface BackendErrorResponse {
  message?: string;
  error?: string;
  errors?: string[] | Record<string, string[]>;
  details?: unknown;
  statusCode?: number;
}

const PASSWORD_LENGTH_POLICY_MESSAGE = 'Password must be at least 12 characters';

const normalizeBackendMessage = (message: string): string => {
  const normalized = message.trim();
  if (!normalized) {
    return normalized;
  }

  const consentMessage = normalizeClientConsentAccessMessage(normalized);
  if (consentMessage !== normalized) {
    return consentMessage;
  }

  if (normalized === PASSWORD_LENGTH_POLICY_MESSAGE) {
    return 'Password must be at least 12 characters long.';
  }

  return normalized;
};

const extractMessagesFromDetails = (details: unknown): string[] => {
  if (!Array.isArray(details)) {
    return [];
  }

  const messages: string[] = [];
  for (const entry of details) {
    if (typeof entry === 'string' && entry.trim()) {
      messages.push(entry);
      continue;
    }

    if (!entry || typeof entry !== 'object') {
      continue;
    }

    const record = entry as Record<string, unknown>;
    if (typeof record.message === 'string' && record.message.trim()) {
      messages.push(record.message);
    }
  }

  return messages;
};

const extractMessagesFromErrorsObject = (errors: Record<string, unknown>): string[] => {
  const messages: string[] = [];
  for (const value of Object.values(errors)) {
    if (typeof value === 'string' && value.trim()) {
      messages.push(value);
      continue;
    }

    if (!Array.isArray(value)) {
      continue;
    }

    for (const item of value) {
      if (typeof item === 'string' && item.trim()) {
        messages.push(item);
      }
    }
  }
  return messages;
};

/**
 * Extracts error message from various error formats
 */
export const extractErrorMessage = (error: unknown): string => {
  // Default fallback message
  const defaultMessage = 'An unexpected error occurred. Please try again.';

  if (!error) return defaultMessage;

  // Handle Axios errors
  if (isAxiosError(error)) {
    const responseData = error.response?.data as BackendErrorResponse | undefined;
    const detailMessages = extractMessagesFromDetails(responseData?.details);

    // Try to get message from response data
    if (responseData) {
      // Check for message field
      if (responseData.message) {
        const normalizedMessage = normalizeBackendMessage(responseData.message);
        if (
          detailMessages.length > 0
          && normalizedMessage.toLowerCase() === 'validation failed'
        ) {
          return detailMessages.map(normalizeBackendMessage).join(', ');
        }
        return normalizedMessage;
      }

      // Check for error field
      if (responseData.error) {
        const normalizedError = normalizeBackendMessage(responseData.error);
        if (
          detailMessages.length > 0
          && normalizedError.toLowerCase() === 'validation failed'
        ) {
          return detailMessages.map(normalizeBackendMessage).join(', ');
        }
        return normalizedError;
      }

      // Check for errors array
      if (responseData.errors) {
        if (Array.isArray(responseData.errors)) {
          return responseData.errors.map(normalizeBackendMessage).join(', ');
        }

        // Handle validation errors object
        if (typeof responseData.errors === 'object') {
          const errorMessages = extractMessagesFromErrorsObject(
            responseData.errors as Record<string, unknown>
          );
          if (errorMessages.length > 0) {
            return errorMessages.map(normalizeBackendMessage).join(', ');
          }
        }
      }

      if (detailMessages.length > 0) {
        return detailMessages.map(normalizeBackendMessage).join(', ');
      }

      if (typeof responseData.details === 'string' && responseData.details.trim()) {
        return normalizeBackendMessage(responseData.details);
      }

      if (typeof error.response?.data === 'string' && error.response.data.trim()) {
        return normalizeBackendMessage(error.response.data);
      }

      if (responseData.statusCode && error.message) {
        const normalizedAxiosMessage = normalizeBackendMessage(error.message);
        if (normalizedAxiosMessage) {
          return normalizedAxiosMessage;
        }
      }
    }

    // Fallback to status text
    if (error.response?.statusText) {
      return error.response.statusText;
    }

    // Network error
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return normalizeBackendMessage(error.message || defaultMessage);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return normalizeBackendMessage(error);
  }

  return defaultMessage;
};

/**
 * Type guard for Axios errors
 */
const isAxiosError = (error: unknown): error is AxiosError => {
  return (error as AxiosError)?.isAxiosError === true;
};

/**
 * Checks if an error is related to minimum energy intake validation
 * @param error - The error from API call
 * @returns true if the error is about minimum energy intake
 */
export const isMinimumEnergyIntakeError = (error: unknown): boolean => {
  if (!error) return false;

  if (isAxiosError(error)) {
    const responseData = error.response?.data as any;
    
    // Check for validationWarnings array format (new format)
    if (Array.isArray(responseData?.validationWarnings)) {
      const hasMinimumEnergyWarning = responseData.validationWarnings.some((warning: any) => {
        return warning.warningType === 'minimum_energy_threshold' || 
               (warning.canOverride === true && 
                warning.message?.toLowerCase().includes('minimum safe threshold'));
      });
      
      if (hasMinimumEnergyWarning) {
        return true;
      }
    }
    
    const errorMessage = extractErrorMessage(error).toLowerCase();
    
    // Check for common minimum energy intake error patterns
    const minimumEnergyKeywords = [
      'minimum energy intake',
      'minimum energy',
      'energy intake',
      'calories are less than minimum',
      'calories less than minimum',
      'below minimum',
      'below the minimum',
      'minimum safe threshold',
      'below the minimum safe threshold',
      'below minimum safe threshold',
      'minimum calories',
      'energy below minimum',
      'below minimum safe',
      'minimum required'
    ];
    
    // Also check the error field directly if it exists
    const directError = responseData?.error?.toLowerCase() || '';
    const directMessage = responseData?.message?.toLowerCase() || '';
    
    // Check details array if it exists
    let detailsError = '';
    if (Array.isArray(responseData?.details)) {
      detailsError = responseData.details
        .map((d: any) => d.message?.toLowerCase() || '')
        .join(' ');
    }
    
    // Also check if details array has minimumRequired field (strong indicator)
    const hasMinimumRequired = Array.isArray(responseData?.details) && 
      responseData.details.some((d: any) => d.minimumRequired !== undefined);
    
    const allErrorText = [errorMessage, directError, directMessage, detailsError].join(' ');
    const keywordMatch = minimumEnergyKeywords.some(keyword => allErrorText.includes(keyword));
    
    return keywordMatch || hasMinimumRequired;
  }

  return false;
};

/**
 * Shows an error toast with the backend error message
 * @param error - The error from API call (Axios error or any error)
 * @param title - Custom title for the toast (defaults to "Error")
 */
export const showErrorToast = (
  error: unknown,
  title: string = 'Error',
  options?: { duration?: number }
): void => {
  const message = [extractErrorMessage(error), ...clinicalErrorIssueMessages(error)].join('\n\n');

  toast({
    title,
    description: message,
    variant: 'destructive',
    duration: options?.duration,
  });
};

/**
 * Shows a success toast
 * @param title - Title for the toast
 * @param description - Description for the toast (optional)
 */
export const showSuccessToast = (
  title: string,
  description?: string
): void => {
  toast({
    title,
    description,
  });
};

export type NotesSavedScope = 'recipe' | 'plan' | 'day' | 'meal' | 'item' | 'lab';

/** Consistent success toast for note save/clear actions across the app. */
export const showNotesSavedToast = (
  scope: NotesSavedScope = 'recipe',
  notes?: string | null,
): void => {
  const cleared = notes == null || String(notes).trim() === '';
  if (cleared) {
    showSuccessToast('Notes cleared', 'Your notes have been removed successfully.');
    return;
  }

  switch (scope) {
    case 'plan':
      showSuccessToast('Plan notes saved', 'Your meal plan notes have been updated successfully.');
      return;
    case 'day':
      showSuccessToast('Day notes saved', 'Your notes for this day have been updated successfully.');
      return;
    case 'meal':
      showSuccessToast('Meal notes saved', 'Your notes for this meal have been updated successfully.');
      return;
    case 'item':
      showSuccessToast('Item notes saved', 'Your notes for this item have been updated successfully.');
      return;
    case 'lab':
      showSuccessToast('Lab notes saved', 'Your lab report notes have been updated successfully.');
      return;
    case 'recipe':
    default:
      showSuccessToast('Recipe notes saved', 'Your notes for this recipe have been updated successfully.');
  }
};

export interface NutritionTargetUpdateNotice {
  title?: string;
  description?: string;
  updatedTargets?: string[];
  reasons?: string[];
}

const asRecord = (value: unknown): Record<string, unknown> | null => {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
};

export const getNutritionTargetUpdateNotice = (
  payload: unknown
): NutritionTargetUpdateNotice | null => {
  const root = asRecord(payload);
  if (!root) return null;

  const nested = asRecord(root.data);
  const notice = asRecord(root.nutritionTargetUpdate) ?? asRecord(nested?.nutritionTargetUpdate);
  if (!notice) return null;

  const description = typeof notice.description === 'string' ? notice.description.trim() : '';
  if (!description) return null;

  return {
    title: typeof notice.title === 'string' && notice.title.trim()
      ? notice.title.trim()
      : 'Nutrition targets updated',
    description,
    updatedTargets: Array.isArray(notice.updatedTargets)
      ? notice.updatedTargets.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : undefined,
    reasons: Array.isArray(notice.reasons)
      ? notice.reasons.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      : undefined,
  };
};

export const showNutritionTargetUpdateToast = (payload: unknown): boolean => {
  const notice = getNutritionTargetUpdateNotice(payload);
  if (!notice) return false;

  toast({
    title: notice.title ?? 'Nutrition targets updated',
    description: notice.description,
    duration: 3000,
  });
  return true;
};

export const scheduleNutritionTargetUpdateToast = (payload: unknown): boolean => {
  const notice = getNutritionTargetUpdateNotice(payload);
  if (!notice) return false;

  globalThis.setTimeout(() => {
    showNutritionTargetUpdateToast({ nutritionTargetUpdate: notice });
  }, 0);
  return true;
};

/**
 * Shows a warning toast with yellow/amber styling
 * @param title - Title for the toast
 * @param description - Description for the toast (optional)
 */
export const showWarningToast = (
  title: string,
  description?: string,
  options?: { duration?: number }
): void => {
  toast({
    title,
    description,
    variant: 'warning',
    duration: options?.duration,
  });
};
